import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { usePatchAhuTunnelMutation } from 'app/services/ahuBoardApi';
import { setActiveTunnelIndex } from 'app/slices/ahuBoardSlice';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { validateTunnel } from '../validation/validationController';
import { formatTunnelAddress, getVisibleTunnelIndexes, tunnelAddressRank } from '../addressing';
import { airflowFlipPatch } from './hybridXOffset';
import { refetchHybridLugPlacements } from '../boardActions';

const MAX_VISIBLE_TUNNELS = 5;

interface TunnelIndexesProps {
  tunnels: IAhuBoardTunnel[];
  /** Hybrid AHU (KA-x) — pills show the full row:level:position address
   * (each pill isolates one physical tunnel, so back-to-back peers need
   * their own distinct label, unlike the grouped Data Bar tunnel list). */
  isHybrid?: boolean;
  /** Used only to find which tunnel owns the currently selected section, for
   * the persistent `.selected-tunnel` mark (independent of the isolation
   * filter below). */
  sections: IAhuBoardSection[];
}

/**
 * Phase 6b — #tunnels-indexes pills (tmpl/canvas/tunnel.indexes.tmpl §2.3,
 * re-homed from the canvas's jQuery render): `all` pill + one per tunnel
 * showing tunnel.index ("1:1"), `.active` from the slice (active_id —
 * 'all' when none isolated). Rendered only when ≥2 tunnels AND the canvas
 * has asked for them (renderTunnelsIndexes → tunnelIndexesVisible).
 *
 * Clicks are the MainView contract (§2.4 changeIndex): `.active` swap +
 * `canvas:change:tunnel:index (id, prev_id)` on the bus — the 6b canvas
 * consumes it (changeIndex → hideOrShowTunnels over the 6c registries).
 *
 * KA-x-user-skill additions ported here (renderTunnelsIndexes/
 * renderTunnelIndexesHtml/markSelectedTunnel, canvas.view3d.js):
 * - row:level:position sort (matches the Data Bar order, not insertion
 *   order), and 3-field addressing only for hybrid AHUs.
 * - Pagination: at most MAX_VISIBLE_TUNNELS pills shown at once, windowed
 *   and re-centered on whichever of the isolation filter / the currently
 *   selected section's tunnel last changed. Pure arrow clicks shift the
 *   window WITHOUT re-centering (mirrors legacy's shiftTunnelIndexesWindow
 *   vs ensureTunnelIndexWindowVisible split).
 * - `.selected-tunnel`: marks the tab for the tunnel that owns the
 *   currently selected section, independent of the isolation filter (which
 *   stays on "all" unless the user explicitly isolates a tunnel).
 *
 * - the selected tab's own inline Reverse Direction toggle (the pair of
 *   `.btn-tunnel-airflow-direction` labels under the label, one per
 *   direction with the inactive one `.hide`-den — canvas.view3d.js
 *   `airflowDirection`). It duplicates the mutation ConfigureTunnel.tsx and
 *   AhuCanvas.tsx already own, which is why 6b left it out; it is a
 *   user-visible affordance in KA-x-user-skill all the same, so it is back.
 *
 * NOT ported: the commented-out `.btn-tunnel-x-anchor` shift-left/right
 * buttons in tunnels.section.tmpl — dead markup in KA-x-user-skill itself
 * (wrapped in a Handlebars comment), never actually shipped there either.
 */
const TunnelIndexes = ({ tunnels, isHybrid, sections }: TunnelIndexesProps) => {
  const dispatch = useAppDispatch();
  const [patchTunnel, { isLoading: airflowPending }] = usePatchAhuTunnelMutation();
  const airflowInFlight = useRef(false);
  const visible = useAppSelector((s) => s.ahuBoard.canvas.tunnelIndexesVisible);
  const activeId = useAppSelector((s) => s.ahuBoard.canvas.activeTunnelIndexId);
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  const [visibleStart, setVisibleStart] = useState(0);

  const sorted = useMemo(
    () =>
      [...tunnels].sort((a, b) => {
        const ra = tunnelAddressRank(a);
        const rb = tunnelAddressRank(b);
        for (let i = 0; i < 3; i++) {
          if (ra[i] !== rb[i]) return ra[i] - rb[i];
        }
        return 0;
      }),
    [tunnels]
  );

  const selectedTunnelId = useMemo(() => {
    if (currentSectionId == null) return null;
    return sections.find((s) => s.id === currentSectionId)?.tunnel_id ?? null;
  }, [sections, currentSectionId]);

  // ensureTunnelIndexWindowVisible (canvas.view3d.js) — re-centers on
  // whichever of the isolation filter or the selected section's tunnel is
  // live right now (isolation wins when both apply, same as legacy since
  // markSelectedTunnel no-ops while the isolation filter already targets a
  // tunnel — real_index still resolves off activeTunnelIndex there).
  useEffect(() => {
    const targetId = activeId !== 'all' ? activeId : selectedTunnelId;
    const targetIndex = targetId != null ? sorted.findIndex((t) => t.id === targetId) : -1;
    setVisibleStart(getVisibleTunnelIndexes(targetIndex >= 0 ? targetIndex : 0, sorted.length, MAX_VISIBLE_TUNNELS).start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, selectedTunnelId, sorted.length]);

  if (!visible || tunnels.length < 2) return <div id="tunnels-indexes" />;

  const total = sorted.length;
  const showNav = total > MAX_VISIBLE_TUNNELS;
  const maxStart = Math.max(0, total - MAX_VISIBLE_TUNNELS);
  const start = Math.max(0, Math.min(visibleStart, maxStart));
  const end = Math.min(total, start + MAX_VISIBLE_TUNNELS);
  const centerSlot = Math.floor(MAX_VISIBLE_TUNNELS / 2);
  const activeIndex = activeId !== 'all' ? sorted.findIndex((t) => t.id === activeId) : -1;
  const visibleTunnels = sorted.slice(start, end);

  // MainView.changeIndex (main.js:116): fires even on the already-active pill
  // (re-click of 'all'/same tunnel re-runs hideOrShowTunnels) — kept verbatim.
  const onPillClick = (id: number | 'all') => {
    const prev = activeId;
    dispatch(setActiveTunnelIndex(id));
    ahuBoardBus.emit('canvas:change:tunnel:index', id, prev);
  };

  // shiftTunnelIndexesWindow (canvas.view3d.js) — shifts the visible window
  // WITHOUT touching which tunnel is active/selected.
  const shiftWindow = (direction: number) => {
    setVisibleStart(Math.max(0, Math.min(maxStart, start + direction)));
  };

  // canvas.view3d.js `airflowDirection` — same PATCH as ConfigureTunnel's
  // toggle (model/tunnel.js `toggle_airflow_direction`, one in flight at a
  // time; the hybrid x_offset re-base lives in the shared airflowFlipPatch,
  // exactly as legacy kept it in the one model method all three affordances
  // called). stopPropagation matters: the icon sits inside the pill, and
  // without it flipping the direction would also isolate that tunnel in 3D.
  const toggleAirflow = (e: React.MouseEvent, tunnel: IAhuBoardTunnel) => {
    e.stopPropagation();
    if (airflowInFlight.current) return;
    airflowInFlight.current = true;
    patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: airflowFlipPatch(tunnel, !!isHybrid) })
      .unwrap()
      .then(() => {
        // Hybrid: the flip restates the tunnel's lug placement server-side —
        // `valid_kinds` mirrors upstream/downstream and the base groups can be
        // rebuilt off the renumbered addressing. Re-read it for the whole unit
        // (refetchHybridLugPlacements).
        void dispatch(refetchHybridLugPlacements(tunnel.ahu_id, { hideWhilePending: true }));
        ahuBoardBus.emit('change:tunnel:dimensions', {
          tunnel_id: tunnel.id,
          render: true,
          accessories_fetch: false,
        });
        void dispatch(validateTunnel(tunnel.id));
      })
      .catch(() => undefined)
      .finally(() => {
        airflowInFlight.current = false;
      });
  };

  return (
    <div id="tunnels-indexes">
      <div
        data-id="all"
        className={`tunnel-index${activeId === 'all' ? ' active' : ''}`}
        onClick={() => onPillClick('all')}
      >
        all
      </div>
      <div className="tunnel-indexes-list">
        {visibleTunnels.map((t, i) => {
          const realIndex = start + i;
          const isActive = realIndex === activeIndex;
          const isCentered = total > MAX_VISIBLE_TUNNELS && isActive && realIndex - start === centerSlot;
          const isSelected = selectedTunnelId != null && t.id === selectedTunnelId;
          return (
            <div
              key={t.id}
              data-index={realIndex}
              data-id={t.id}
              className={`tunnel-index${isActive ? ' active' : ''}${isCentered ? ' tunnel-index-centered' : ''}${
                isSelected ? ' selected-tunnel' : ''
              }`}
              onClick={() => onPillClick(t.id)}
            >
              {isHybrid ? formatTunnelAddress(t, true) : t.index}
              {isSelected && (
                <>
                  <label
                    id={String(t.id)}
                    className={`icon-airflow-direction-icon btn-tunnel-airflow-direction ka-has-tooltip left-to-right left-to-right-${t.id}${
                      t.airflow_direction_id === 2 ? ' hide' : ''
                    }${airflowPending ? ' non-clickable' : ''}`}
                    aria-disabled={airflowPending}
                    {...{ tooltip: 'Reverse Direction' }}
                    onClick={(e) => toggleAirflow(e, t)}
                  />
                  <label
                    id={String(t.id)}
                    className={`icon-airflow-direction-icon btn-tunnel-airflow-direction ka-has-tooltip right-to-left right-to-left-${t.id}${
                      t.airflow_direction_id === 1 ? ' hide' : ''
                    }${airflowPending ? ' non-clickable' : ''}`}
                    aria-disabled={airflowPending}
                    {...{ tooltip: 'Reverse Direction' }}
                    onClick={(e) => toggleAirflow(e, t)}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
      {showNav && (
        <div className="tunnel-indexes-nav">
          <label
            className={`icon-chevron-left tunnel-indexes-arrow tunnel-indexes-arrow-left${start <= 0 ? ' disabled' : ''}`}
            onClick={() => start > 0 && shiftWindow(-1)}
          />
          <label
            className={`icon-chevron-right tunnel-indexes-arrow tunnel-indexes-arrow-right${end >= total ? ' disabled' : ''}`}
            onClick={() => end < total && shiftWindow(1)}
          />
        </div>
      )}
    </div>
  );
};

export default TunnelIndexes;
